import * as React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { InsuranceProgramFragment } from '@websky/graphql';
import { CartServiceInsurance, OnDeleteItemType } from '../../../Cart/types';
import { InsurancePageModal, InsuranceUpsaleModal } from '../../../InsurancePage';
import { ThemeProvider, useTheme } from '../../../theme';
import { Money } from '../../../Money';
import { useInsuranceGroupTexts, useInsuranceUpsale } from './hooks';
import SimpleLoader from '../../../SimpleLoader';
import Modal from '../../../Modal';
import ServiceSummaryDivider from '../../../ServiceSummary/components/ServiceSummaryDivider/ServiceSummaryDivider';
import ServiceSummaryOption from '../../../ServiceSummary/components/ServiceSummaryOption';
import InsuranceSummary from '../../../ServiceSummary/services/InsuranceSummary';
import { BaseButton } from '../../../BaseComponents';
import { OptionsList } from '../../../InsurancePage/components/OptionsList/OptionsList';
import { getInsuranceGroupNameAttr, getInsuranceTypeAttr } from './utils';
import { CdnIcon, SafeHtml } from '@shared/ui';

export interface Props {
	insurancePrograms?: InsuranceProgramFragment[];
	groupName?: string;
	oldVersion?: boolean;
	segmentId?: string;
}

const InsuranceUpsale: React.FC<Props> = ({ insurancePrograms, oldVersion, groupName }) => {
	const { t } = useTranslation('Checkout');
	const { InsuranceUpsale: theme } = useTheme('CheckoutAdditionalService');

	const model = useInsuranceUpsale(insurancePrograms);
	const groupTexts = useInsuranceGroupTexts(groupName, insurancePrograms);

	const onServiceDelete: OnDeleteItemType = async (_, service) => {
		const insuranceService = service as CartServiceInsurance;

		await model.onDeleteInsurance(insuranceService.code);
	};

	const renderPriceDescription = (isSelected: boolean) => {
		return isSelected ? (
			model.totalPrice.amount > 0 && (
				<div className={theme.price}>
					<span className={theme.price__label}>{t('Total')}</span>
					<Money moneyClassName={theme.money} money={model.totalPrice} />
					<CdnIcon icon={'Info/Item Icluded'} className={theme.includedIcon} />
				</div>
			)
		) : (
			<>
				<span className={theme.forAll}>{t('For all passengers')}</span>
				<div className={theme.price}>
					{model.isDifferentPrices && <span className={theme.price__label}>{t('from')}</span>}
					<Money moneyClassName={theme.money} money={model.minPrice} />
				</div>
			</>
		);
	};

	return (
		<>
			<InsuranceSummary
				onServiceDelete={model.isReadonly || model.totalPrice.amount === 0 ? null : onServiceDelete}
				selectedInsurances={model.selectedPrograms}
				isConfirmed={model.isConfirmed}
				isDownload
				onDownloadVskInsurance={model.onDownloadVskInsurance}
			>
				{items => {
					const isSelected = items.length > 0;

					return (
						<ThemeProvider
							value={{
								ServiceSummary: {
									ServiceSummaryOption: {
										option__name: theme.option__name,
										option__price: theme.option__price,
										option__price_icon: theme.option__price_icon,
										option__download: theme.option__download,
										option__iconIncluded: theme.option__iconIncluded
									}
								}
							}}
						>
							<div
								className={cn(
									theme.container,
									{
										[theme.container_selected]: isSelected,
										[theme.oldVersion]: oldVersion,
										[theme.container_loading]: model.isLoading
									},
									groupName
								)}
								{...getInsuranceGroupNameAttr(groupName)}
								{...getInsuranceTypeAttr(insurancePrograms)}
							>
								<div className={theme.header}>
									{groupTexts.title}
									{oldVersion && renderPriceDescription(isSelected)}
								</div>
								<div className={theme.content}>
									{isSelected && model.order.travellers.length > 1 && (
										<div className={theme.label}>{t('For all passengers')}</div>
									)}
									{isSelected ? (
										<ServiceSummaryDivider>
											<ServiceSummaryOption serviceType="Insurance" groupOption={{ items }} />
										</ServiceSummaryDivider>
									) : (
										<SafeHtml tag="span" html={groupTexts.descr} />
									)}
									{!isSelected && (
										<OptionsList className={theme.options} options={groupTexts.options} />
									)}
								</div>
								<div className={theme.footer}>
									{!oldVersion && (
										<div className={theme.info}>{renderPriceDescription(isSelected)}</div>
									)}
									<div className={theme.controls}>
										<BaseButton
											className={cn(theme.button, {
												[theme.change]: isSelected,
												[theme.loading]: model.isLoading
											})}
											variant={isSelected ? 'optionEdit' : 'optionAction'}
											onClick={
												model.isSingleToggleMode
													? isSelected
														? model.onDeleteSingle
														: model.onAddSingle
													: model.open
											}
											isLoading={model.isLoading}
										>
											{!isSelected
												? t('Add protection')
												: model.isReadonly
												? t('More details')
												: model.isSingleToggleMode
												? t('Remove protection')
												: t('Change')}
										</BaseButton>
									</div>
								</div>
							</div>
						</ThemeProvider>
					);
				}}
			</InsuranceSummary>

			<Modal classes={{ scrollBody: theme.scrollBody, paper: theme.paper }} open={model.isOpen} fullScreen>
				<InsurancePageModal
					availablePrograms={model.availablePrograms}
					onServicesChanged={model.setIsNeedToSendSaveReq}
					onTotalInsurancePriceChange={model.setInsurancePriceToPay}
					onSaveOrderServicesParamsChange={model.setSaveOrderServicesParams}
					onDownloadNasInsurance={model.onDownloadVskInsurance}
					isShowImportantInsurancesError={model.isShowImportantInsurancesError}
					totalPrice={model.insurancePriceToPay}
					onConfirm={model.onConfirm}
					onClose={model.onClose}
				/>
				{model.isLoading && <SimpleLoader />}
			</Modal>

			<InsuranceUpsaleModal
				insurance={model.priorityInsurance}
				isOpen={model.isUpsaleOpen}
				onAgree={model.handleAgree}
				onDisagree={model.handleDisagree}
			/>
		</>
	);
};

export default InsuranceUpsale;
